import React, { useMemo } from 'react';
import UnifiedCalendarItem from './UnifiedCalendarItem';
import type { UnifiedCalendarItemProps } from './UnifiedCalendarItem';

type CalendarEntry = UnifiedCalendarItemProps['entry'] & {
  start_date?: string;
  end_date?: string;
};

interface CalendarWeekViewProps {
  entries: CalendarEntry[];
  selectedDate: Date | string;
  isDark: boolean;
  cardClass: string;
  onDateChange: (date: Date) => void;
  onEntryClick?: (entry: CalendarEntry) => void;
}

const DAY_NAMES_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function toDate(d: Date | string): Date {
  if (d instanceof Date) return d;
  const [y, m, day] = d.split('-').map(Number);
  if (y && m && day) return new Date(y, m - 1, day);
  return new Date(d);
}

function toLocalDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfWeek(d: Date): Date {
  const result = new Date(d);
  result.setDate(d.getDate() - d.getDay());
  result.setHours(0, 0, 0, 0);
  return result;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

const CalendarWeekView: React.FC<CalendarWeekViewProps> = ({
  entries,
  selectedDate,
  isDark,
  cardClass,
  onDateChange,
  onEntryClick,
}) => {
  const date = useMemo(() => toDate(selectedDate), [selectedDate]);
  const weekStart = useMemo(() => startOfWeek(date), [date]);

  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }).map((_, i) => {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      return d;
    });
  }, [weekStart]);

  const entriesByDay = useMemo(() => {
    return weekDays.map((day) => {
      const dayKey = toLocalDateKey(day);
      return entries.filter((entry) => {
        if (entry.type === 'time_off' && entry.start_date && entry.end_date) {
          return entry.start_date <= dayKey && entry.end_date >= dayKey;
        }
        if (entry.dueDate) {
          return entry.dueDate.slice(0, 10) === dayKey;
        }
        return false;
      });
    });
  }, [entries, weekDays]);

  const goToPrev = () => {
    const prev = new Date(weekStart);
    prev.setDate(weekStart.getDate() - 7);
    onDateChange(prev);
  };

  const goToNext = () => {
    const next = new Date(weekStart);
    next.setDate(weekStart.getDate() + 7);
    onDateChange(next);
  };

  const goToToday = () => onDateChange(new Date());

  const today = new Date();
  const isCurrentWeek = weekDays.some((d) => isSameDay(d, today));
  const weekEnd = weekDays[6];

  const rangeLabel =
    weekStart.getMonth() === weekEnd.getMonth()
      ? `${MONTH_NAMES[weekStart.getMonth()]} ${weekStart.getDate()} – ${weekEnd.getDate()}, ${weekEnd.getFullYear()}`
      : `${MONTH_NAMES[weekStart.getMonth()]} ${weekStart.getDate()} – ${MONTH_NAMES[weekEnd.getMonth()]} ${weekEnd.getDate()}, ${weekEnd.getFullYear()}`;

  const navBtnClass = isDark
    ? 'px-3 py-1.5 rounded text-sm font-medium bg-slate-700 text-slate-200 hover:bg-slate-600 transition-colors'
    : 'px-3 py-1.5 rounded text-sm font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors';

  return (
    <div className={`${cardClass} rounded-xl p-4 flex flex-col gap-4`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={goToPrev}
            className={navBtnClass}
            aria-label="Previous week"
          >
            &#8249;
          </button>
          <button
            type="button"
            onClick={goToNext}
            className={navBtnClass}
            aria-label="Next week"
          >
            &#8250;
          </button>
          {!isCurrentWeek && (
            <button
              type="button"
              onClick={goToToday}
              className={`${navBtnClass} ml-1`}
            >
              Today
            </button>
          )}
        </div>
        <h2
          className={`text-base sm:text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
        >
          {rangeLabel}
        </h2>
      </div>

      {/* Week grid - desktop */}
      <div className="hidden md:grid grid-cols-7 gap-2">
        {weekDays.map((day, idx) => {
          const isToday = isSameDay(day, today);
          const dayEntries = entriesByDay[idx];
          return (
            <div
              key={toLocalDateKey(day)}
              className={`border rounded-lg p-2 min-h-[140px] flex flex-col ${
                isToday
                  ? isDark
                    ? 'border-violet-500/50 bg-violet-500/10'
                    : 'border-violet-300 bg-violet-50'
                  : isDark
                    ? 'border-slate-700/50 bg-slate-800/30'
                    : 'border-slate-200 bg-white'
              }`}
            >
              <button
                type="button"
                onClick={() => onDateChange(day)}
                className={`text-left mb-2 ${isDark ? 'text-slate-300 hover:text-white' : 'text-slate-700 hover:text-slate-900'}`}
              >
                <div
                  className={`text-xs font-semibold ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
                >
                  {DAY_NAMES_SHORT[day.getDay()]}
                </div>
                <div
                  className={`text-lg font-bold ${
                    isToday
                      ? isDark
                        ? 'text-violet-400'
                        : 'text-violet-600'
                      : ''
                  }`}
                >
                  {day.getDate()}
                </div>
              </button>
              <div className="flex flex-col gap-1 overflow-hidden">
                {dayEntries.map((entry) => (
                  <UnifiedCalendarItem
                    key={`${entry.type}-${entry.id}`}
                    entry={entry}
                    isDark={isDark}
                    onClick={
                      onEntryClick ? () => onEntryClick(entry) : undefined
                    }
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Week list - mobile */}
      <div className="md:hidden flex flex-col gap-3">
        {weekDays.map((day, idx) => {
          const isToday = isSameDay(day, today);
          const dayEntries = entriesByDay[idx];
          return (
            <div
              key={toLocalDateKey(day)}
              className={`border rounded-lg p-2 ${
                isToday
                  ? isDark
                    ? 'border-violet-500/50 bg-violet-500/10'
                    : 'border-violet-300 bg-violet-50'
                  : isDark
                    ? 'border-slate-700/50 bg-slate-800/30'
                    : 'border-slate-200 bg-white'
              }`}
            >
              <button
                type="button"
                onClick={() => onDateChange(day)}
                className={`flex items-center gap-2 mb-2 ${isDark ? 'text-slate-200' : 'text-slate-800'}`}
              >
                <span className="text-xs font-semibold uppercase">
                  {DAY_NAMES_SHORT[day.getDay()]}
                </span>
                <span
                  className={`text-base font-bold ${
                    isToday
                      ? isDark
                        ? 'text-violet-400'
                        : 'text-violet-600'
                      : ''
                  }`}
                >
                  {day.getDate()}
                </span>
                <span
                  className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
                >
                  ({dayEntries.length})
                </span>
              </button>
              {dayEntries.length > 0 && (
                <div className="flex flex-col gap-1">
                  {dayEntries.map((entry) => (
                    <UnifiedCalendarItem
                      key={`${entry.type}-${entry.id}`}
                      entry={entry}
                      isDark={isDark}
                      onClick={
                        onEntryClick ? () => onEntryClick(entry) : undefined
                      }
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CalendarWeekView;
